/**
 * The `/pools` list pane. Reads `/pool/list-mine` through the generated hook
 * and draws the search field, the two actions, the card stack, and the
 * loading / empty / no-results / failed states. The route owns the URL
 * state (`?q=`, `?poolId=`, `?dialog=`); this component takes plain props.
 */
import { usePoolListMine } from "@sportsmet/pool-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import {
  Button,
  FOCUS_RING,
  InlineBanner,
  Input,
  Skeleton,
} from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { PoolInvitationsLink } from "../invites/PoolInvitationsLink";
import { InvitedPoolsStrip } from "../invites/InvitedPoolsStrip";
import { PoolCard } from "./PoolCard";
import {
  isCreatePoolAvailable,
  leagueAbbreviationsByKey,
  resolvePoolsListState,
} from "./poolsListState";

/**
 * ISSUE-782, Q-782-A answer B. The same sentence the wizard's first step used
 * to show once a fan was already inside it; it now sits beside the disabled
 * control instead, so the fan reads it before pressing rather than after.
 */
export const CREATE_POOL_NO_LEAGUES_NOTE =
  "No leagues are open for new pools right now. Check back soon.";

const CREATE_POOL_NOTE_ID = "pools-create-note";

/**
 * ISSUE-912, D2. The three things a fan can be told about a failed pools
 * read, as three separate sentences, because a screen reader only re-reads a
 * live region whose text actually changed.
 *
 * The banner carries the first one and never changes, so it is announced once
 * and stays readable. The retry line below it carries the other two and
 * changes on every attempt: empty, then "Trying again.", then the repeat
 * failure, then "Trying again." again. A fan who taps Try again against a
 * server that is still down hears something every single time.
 */
export const POOLS_FAILED_MESSAGE = "We could not load your pools.";
export const POOLS_RETRY_BUSY_MESSAGE = "Trying again.";
export const POOLS_RETRY_FAILED_MESSAGE =
  "That did not work. Your pools are still not loading.";

/**
 * ISSUE-199. The same words the mobile entry point uses
 * (`apps/mobile/app/(tabs)/pools/index.tsx`), so a fan who learned the flow on
 * one client recognizes it on the other.
 */
export const FIND_POOL_LABEL = "Find a pool";

/**
 * ISSUE-1402, the web's lighter twin of the phone's "Have a pool invite?"
 * sheet. A fan who signs up from a friend's invite and lands here with no
 * pools reads where the invite goes: Join a pool opens the dialog with Paste
 * invite and the code field. A line, not a second card, so the empty state
 * keeps one primary action (`single-primary-cta.mdc`).
 */
export const POOLS_EMPTY_INVITE_HINT =
  "Have an invite from a friend? Tap Join a pool and paste the link or code.";

/**
 * ISSUE-1343. v1's Pools top bar Archive action, as a quiet link under the
 * list: the pools this fan archived for themselves live one screen away.
 * Shown whatever the list holds, because a fan who archived every pool
 * lands on the empty state and still needs the way back.
 */
export const ARCHIVED_POOLS_LINK_LABEL = "Archived pools";
const ARCHIVED_POOLS_LINK_CLASS = `inline-flex min-h-touch-target items-center self-center text-body-strong text-primary underline-offset-2 hover:underline ${FOCUS_RING}`;

export type typePoolsListPaneProps = {
  /** The `?q=` value, or "" when the param is absent. */
  query: string;
  /** The `?poolId=` value, or undefined when nothing is selected. */
  selectedPoolId: string | undefined;
  onQueryChange: (query: string) => void;
  onSelectPool: (poolId: string) => void;
  onCreatePool: () => void;
  onJoinPool: () => void;
  /**
   * Opens `/pools/search` (ISSUE-199). The field above searches the pools the
   * fan is already in; this opens the search over every pool the server lets
   * them see, which is a different question and a different endpoint.
   */
  onFindPool: () => void;
};

export const PoolsListPane = (props: typePoolsListPaneProps): ReactNode => {
  const pools = usePoolListMine({});
  // ISSUE-770: the league badge reads the catalog's own abbreviation rather
  // than slicing `leagueKey`. `CreatePoolDialog` already fetches this same
  // query unconditionally on every `/pools` mount, so this is a cache hit,
  // not a second network round trip.
  const catalog = useLeagueCatalogGet({});
  const leagueAbbreviations = useMemo(
    () => leagueAbbreviationsByKey(catalog.data?.data?.sports ?? []),
    [catalog.data],
  );
  const canCreatePool = isCreatePoolAvailable({
    isLoading: catalog.isLoading,
    hasFailed: catalog.isError || Boolean(catalog.data?.error),
    sports: catalog.data?.data?.sports ?? null,
  });

  // The generated SDK does not reject on an API error: a failed call RESOLVES
  // with `{ error, data: null }` and react-query files it as a success
  // (`queryClient.ts`). So the failed phase is `data.error` far more often
  // than it is `isError`, and both have to be read to see a failed load.
  const hasFailedRead = pools.isError || Boolean(pools.data?.error);

  const state = resolvePoolsListState({
    isLoading: pools.isLoading,
    hasFailed: hasFailedRead,
    items: pools.data?.data?.items ?? null,
    query: props.query,
    leagueAbbreviations,
  });

  /**
   * ISSUE-912, D2. How many times a retry the fan asked for came back failed,
   * within the failure the fan is looking at right now.
   *
   * `hasFailedRead` cannot answer this on its own: it is already true from the
   * first failure and stays true through every further one, so nothing about
   * the query changes when the second attempt fails the same way. This counts
   * the answers, which is the fact the fan is missing.
   */
  const [failedRetryCount, setFailedRetryCount] = useState(0);

  /**
   * A retry the FAN pressed is in flight.
   *
   * Deliberately not `pools.isFetching`. That is true for any read at all,
   * including the ones the app starts by itself: `queryClient.ts` turns on
   * refetch-on-focus, on-reconnect and on-mount, so a stale failed entry is
   * re-read when the tab comes back or the connection does. Reading the busy
   * state off `isFetching` announced "Trying again." and disabled the control
   * for a read nobody asked for, while the outcome below counts only reads
   * `handleRetry` started, so that announcement of a beginning was never
   * followed by an announcement of an answer. The spinner belongs to the
   * control that was pressed (`scoped-loading-state.mdc`), and a read the fan
   * did not start is not that control's work.
   *
   * Nothing else on this screen is disabled while the retry runs.
   */
  const [isRetrying, setIsRetrying] = useState(false);

  /**
   * A read that comes back clean ends the failure the repeat sentence
   * describes. Without this the count only ever grows, so a later and
   * unrelated failure would mount the live region with that sentence already
   * inside it, which is precisely the text the comment below says a screen
   * reader does not reliably announce.
   */
  useEffect(() => {
    if (hasFailedRead) return;
    setFailedRetryCount(0);
  }, [hasFailedRead]);

  const handleRetry = (): void => {
    setIsRetrying(true);
    void pools.refetch().then(
      (result) => {
        setIsRetrying(false);
        if (result.isError || Boolean(result.data?.error)) {
          setFailedRetryCount((count) => count + 1);
        }
      },
      () => {
        setIsRetrying(false);
        setFailedRetryCount((count) => count + 1);
      },
    );
  };

  let retryStatus = "";
  if (isRetrying) {
    retryStatus = POOLS_RETRY_BUSY_MESSAGE;
  } else if (failedRetryCount > 0) {
    retryStatus = POOLS_RETRY_FAILED_MESSAGE;
  }

  const cachedState = resolvePoolsListState({
    isLoading: false,
    hasFailed: false,
    items: pools.data?.data?.items ?? null,
    query: props.query,
    leagueAbbreviations,
  });

  return (
    <div className="flex flex-col gap-3" data-testid="pools-list-pane">
      {/* ISSUE-1346: invitations waiting, above the fan's own pools. */}
      <PoolInvitationsLink />
      <InvitedPoolsStrip />
      {state.phase !== "empty" ? (
        <>
          <Input
            type="search"
            label="Search your pools"
            labelHidden
            placeholder="Search your pools"
            value={props.query}
            onChange={(event) => {
              props.onQueryChange(event.target.value);
            }}
            data-testid="pools-search"
          />
          {/* flex-wrap, not just flex: a flex item defaults to min-width:auto,
              so `flex-1` alone will not shrink a button below its own label.
              At the largest text step the two labels no longer fit side by
              side and the row pushed the page 5px wider than the viewport.
              Wrapping lets them stack instead of overflowing, and keeps the
              labels readable rather than truncating them. */}
          <div className="flex flex-wrap gap-3">
            <div className="flex min-w-40 flex-1 flex-col gap-1">
              <Button
                fullWidth
                onClick={props.onCreatePool}
                data-testid="pools-create"
                disabled={!canCreatePool}
                aria-describedby={
                  canCreatePool ? undefined : CREATE_POOL_NOTE_ID
                }
              >
                Create pool
              </Button>
              {canCreatePool ? null : (
                <p
                  id={CREATE_POOL_NOTE_ID}
                  className="text-caption text-ink-secondary"
                  data-testid={CREATE_POOL_NOTE_ID}
                >
                  {CREATE_POOL_NO_LEAGUES_NOTE}
                </p>
              )}
            </div>
            <Button
              variant="secondary"
              onClick={props.onJoinPool}
              data-testid="pools-join"
              className="min-w-40 flex-1"
            >
              Join a pool
            </Button>
            {/* ISSUE-199. Third item in the same wrapping row, so it drops to
                its own line at narrow widths instead of squeezing the other
                two. Same label as the mobile entry point. */}
            <Button
              variant="secondary"
              onClick={props.onFindPool}
              data-testid="pools-find"
              className="min-w-40 flex-1"
            >
              {FIND_POOL_LABEL}
            </Button>
          </div>
        </>
      ) : null}

      {state.phase === "loading" ? (
        <div className="flex flex-col gap-3">
          <Skeleton
            shape="card"
            label="Loading your pools"
            data-testid="pools-loading"
          />
          <Skeleton shape="card" />
          <Skeleton shape="card" />
        </div>
      ) : null}

      {state.phase === "failed" ? (
        <div data-testid="pools-failed">
          {/* ISSUE-912. This banner has no dismiss, on purpose. It carries the only
              words that say the load failed and the only Try again, and on a
              first-load failure there are no cached cards under it, so
              dismissing it left the fan a search field and three buttons over
              blank space with nothing to press and no way to tell a broken
              app from an empty list. `InlineBanner` supports a banner that
              must stay put by leaving `onDismiss` off. The fan's way to clear
              it is to succeed: a Try again that loads leaves the failed phase
              and the banner goes with it. */}
          <InlineBanner
            variant="warning"
            message={POOLS_FAILED_MESSAGE}
            action={{
              label: "Try again",
              onAction: handleRetry,
              loading: isRetrying,
              // No `loadingLabel`: the retry line below is this screen's own
              // live region and already says "Trying again." in words that
              // carry on into the answer. A second announcement from the
              // banner would tell the fan the same thing twice.
            }}
          />
          {/* ISSUE-912, D2. Always rendered, empty to start, so it is a live
              region that was already on the page when its words change. A
              region mounted at the same moment as its first message is not
              reliably announced, and a message node that never changes is
              never announced twice, which is how a second failure used to
              reach the fan as silence. */}
          <p
            role="status"
            aria-live="polite"
            data-testid="pools-retry-status"
            className="mt-2 text-caption text-ink-secondary"
          >
            {retryStatus}
          </p>
          {cachedState.phase === "ready" ? (
            <div className="mt-3 flex flex-col gap-3" data-testid="pools-stack">
              {cachedState.cards.map((card) => (
                <PoolCard
                  key={card.poolId}
                  model={card}
                  isSelected={card.poolId === props.selectedPoolId}
                  onSelect={props.onSelectPool}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {state.phase === "empty" ? (
        <div
          className="flex flex-col items-center gap-2 py-10 text-center"
          data-testid="pools-empty"
        >
          <p className="text-body-strong text-ink">No pools yet</p>
          <p className="text-body text-ink-secondary">
            Join a pool to start picking winners.
          </p>
          <p
            className="text-small text-ink-secondary"
            data-testid="pools-empty-invite-hint"
          >
            {POOLS_EMPTY_INVITE_HINT}
          </p>
          <Button
            onClick={props.onJoinPool}
            fullWidth
            className="mt-2 max-w-50"
          >
            Join a pool
          </Button>
          <div className="flex w-full max-w-50 flex-col gap-1">
            <Button
              variant="secondary"
              onClick={props.onCreatePool}
              fullWidth
              data-testid="pools-create"
              disabled={!canCreatePool}
              aria-describedby={canCreatePool ? undefined : CREATE_POOL_NOTE_ID}
            >
              Create pool
            </Button>
            {canCreatePool ? null : (
              <p
                id={CREATE_POOL_NOTE_ID}
                className="text-caption text-ink-secondary"
                data-testid={CREATE_POOL_NOTE_ID}
              >
                {CREATE_POOL_NO_LEAGUES_NOTE}
              </p>
            )}
            {/* ISSUE-199. The empty state replaces the actions row rather
                than sitting beside it, so an entry point that lived only up
                there would be invisible to a fan with no pools yet, which is
                exactly the fan who needs to go find one. */}
            <Button
              variant="secondary"
              onClick={props.onFindPool}
              fullWidth
              data-testid="pools-find-empty"
            >
              {FIND_POOL_LABEL}
            </Button>
          </div>
        </div>
      ) : null}

      {state.phase === "noResults" ? (
        <div
          className="flex flex-col items-center gap-2 py-10 text-center"
          data-testid="pools-no-results"
        >
          <p className="text-body-strong text-ink">{`No pools match "${state.query}"`}</p>
          <p className="text-body text-ink-secondary">Try a different name.</p>
        </div>
      ) : null}

      {state.phase === "ready" ? (
        <div className="flex flex-col gap-3" data-testid="pools-stack">
          {state.cards.map((card) => (
            <PoolCard
              key={card.poolId}
              model={card}
              isSelected={card.poolId === props.selectedPoolId}
              onSelect={props.onSelectPool}
            />
          ))}
        </div>
      ) : null}

      {state.phase === "loading" ? null : (
        <Link
          to="/pools/archived"
          className={ARCHIVED_POOLS_LINK_CLASS}
          data-testid="pools-archived-link"
        >
          {ARCHIVED_POOLS_LINK_LABEL}
        </Link>
      )}
    </div>
  );
};
